import { emptySkillRequires } from "./skill-requires.js";
function cleanList(raw) {
    if (!raw)
        return [];
    const out = [];
    for (const item of raw) {
        const value = item.trim();
        if (value && !out.includes(value))
            out.push(value);
    }
    return out;
}
/**
 * AgentProfile（或其 manifest profile 片段）→ canonical 三段。纯函数、不读盘。
 */
export function toCanonicalAgent(profile) {
    const caps = profile.capabilities;
    const requires = {
        ...emptySkillRequires(),
        tools: cleanList(caps?.dosiaTools),
        connectors: cleanList(caps?.mcpServers),
        native: cleanList(caps?.nativeTools),
    };
    const members = cleanList(caps?.subagents);
    const skills = cleanList(caps?.skills);
    const name = profile.name.trim();
    const description = profile.description?.trim() ?? "";
    const display = {
        name: { zh: name, en: "" },
        ...(description ? { tagline: { zh: description, en: "" } } : {}),
    };
    const canonical = { requires, display };
    if (members.length > 0 || skills.length > 0) {
        canonical.experts = [
            {
                id: (profile.id ?? name).trim(),
                lead: name,
                // 员工在工作台自建的 Agent 没有「成员任务形态」这个概念——它的 `subagents` 就是
                // 一串子 agent 名。恒定 `oneshot`（= 今天的行为：能派就派），形态目前只由插件
                // `experts[]` 的作者显式声明（REQ-250）。写死在这里而不是让 `ExpertMember.shape`
                // 可选：可选就等于把「没说」和「说了 oneshot」混成一件事，下游又要各自兜底。
                members: members.map((agent) => ({ agent, shape: "oneshot" })),
                skills,
                display,
            },
        ];
    }
    return canonical;
}
/**
 * canonical 三段 → 五轴能力面。{@link toCanonicalAgent} 的逆：同一份数据换回
 * AgentProfile 认得的形状（`experts[0]` 就是本 Agent 自己那一条）。
 *
 * 只还原能力面五轴——name/description 是 canonical `display` 的**副本**而不是
 * 权威源，反向时不从 display 倒推回 profile 字段（那会让一次往返把作者改过的
 * 名字覆盖回旧展示文案）。
 */
export function fromCanonicalAgent(canonical) {
    const own = canonical.experts?.[0];
    return {
        skills: cleanList(own?.skills),
        dosiaTools: cleanList(canonical.requires.tools),
        mcpServers: cleanList(canonical.requires.connectors),
        nativeTools: cleanList(canonical.requires.native),
        // 往返逆运算：正向把裸名折成 `{agent, shape:"oneshot"}`，反向取回名字。形态在这条
        // 腿上是恒定值，不进 `capabilities`——把它塞回去会让自建 Agent 长出一个作者从未声明、
        // 也没有 UI 能改的字段。
        subagents: cleanList(own?.members.map((m) => m.agent)),
    };
}
/** 便捷入口：直接从一张工作台 Agent 卡片取 canonical 形态。 */
export function agentProfileToCanonical(profile) {
    return toCanonicalAgent({
        id: profile.id,
        name: profile.name,
        description: profile.description,
        capabilities: profile.capabilities,
    });
}
//# sourceMappingURL=agent-canonical.js.map